/**
 * Channel management: create a channel, browse channels, channel settings, and the sidebar's section editor and
 * "Move to section…" picker. Every sheet renders a view model's state and calls its intents.
 */
import type {
  BrowseChannelsState,
  BrowseChannelsViewModel,
  BrowseFilter,
  ChannelSettingsState,
  ChannelSettingsViewModel,
  CreateChannelState,
  CreateChannelViewModel,
  FolderEditor,
  MovePicker,
} from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import type { NotifyLevel } from "@bundle/contracts";
import { useState, type ReactNode } from "react";
import { Avatar, Button, Card, EmojiPicker, Icon, Popover, Pressable, SegmentedControl, Sheet, Stack, Text, TextField, Toast, Toggle } from "../primitives/index.ts";
import { useLocale, useT, useTr } from "../i18n/I18nProvider.tsx";

function FieldLabel({ label }: { label: string }) {
  return (
    <Text variant="footnote" weight="medium" color="text.secondary">
      {label}
    </Text>
  );
}

// ---------- Create channel ----------

export type CreateChannelSheetProps = {
  open: boolean;
  onClose: () => void;
  vm: CreateChannelViewModel;
  state: CreateChannelState;
  /** The channel exists (or is queued offline): open it. */
  onCreated: (channelId: string) => void;
};

/** Name (lowercase-dash slug, validated live), description, public/private and people to add. */
export function CreateChannelSheet({ open, onClose, vm, state: s, onCreated }: CreateChannelSheetProps) {
  const t = useT();
  const tr = useTr();
  const submit = () => {
    void vm.submit().then((id) => {
      if (id !== undefined) onCreated(id);
    });
  };
  const nameNote = s.showNameError && s.nameError !== undefined ? s.nameError : s.nameHint;
  return (
    <Sheet open={open} onClose={onClose} title={t("channels.create.title")} closeLabel={t("common.close")} testID="create-channel-sheet">
      <Stack gap="4">
        <Stack gap="1">
          <TextField
            label={t("channels.create.name")}
            value={s.name}
            onChange={(v) => vm.setName(v)}
            placeholder={t("channels.create.namePlaceholder")}
            leadingIcon={s.isPrivate ? "lock" : "hash"}
            autoFocus
            onSubmit={submit}
            testID="create-channel-name"
          />
          {nameNote === undefined ? null : (
            <Text variant="caption" color={s.showNameError && s.nameError !== undefined ? "status.danger" : "text.tertiary"} testID="create-channel-name-note">
              {tr(nameNote)}
            </Text>
          )}
        </Stack>
        <TextField
          label={t("channels.create.description")}
          value={s.purpose}
          onChange={(v) => vm.setPurpose(v)}
          placeholder={t("channels.create.descriptionPlaceholder")}
          multiline
          testID="create-channel-description"
        />
        <Stack gap="1">
          <Toggle label={t("channels.create.private")} value={s.isPrivate} onChange={(v) => vm.setPrivate(v)} testID="create-channel-private" />
          <Text variant="caption" color="text.tertiary">
            {s.isPrivate ? t("channels.create.privateHint") : t("channels.create.publicHint")}
          </Text>
        </Stack>
        {s.canAddPeople ? (
          <Stack gap="1.5">
            <FieldLabel label={t("channels.create.members")} />
            {s.selected.length === 0 ? null : (
              <Stack direction="horizontal" gap="1.5" wrap testID="create-channel-selected">
                {s.selected.map((m) => (
                  <Pressable key={m.userId} radius="full" onPress={() => vm.removeMember(m.userId)} accessibilityLabel={t("channels.create.removeMember", { name: m.name })} testID={`create-channel-selected-${m.userId}`}>
                    <Stack direction="horizontal" align="center" gap="1.5" paddingX="2" paddingY="1" radius="full" background="chip.bg">
                      <Avatar name={m.name} src={m.avatarUrl} size="avatar.xs" square={m.isAgent} />
                      <Text variant="footnote" color="chip.fg">
                        {m.name}
                      </Text>
                      <Icon name="close" size="icon.sm" color="text.tertiary" />
                    </Stack>
                  </Pressable>
                ))}
              </Stack>
            )}
            <TextField value={s.query} onChange={(v) => vm.setQuery(v)} placeholder={t("channels.create.membersSearch")} leadingIcon="search" testID="create-channel-people-search" />
            <Stack gap="0.5" testID="create-channel-people">
              {s.people.length === 0 ? (
                <Text variant="footnote" color="text.tertiary">
                  {t("channels.create.membersNone")}
                </Text>
              ) : (
                s.people.map((m) => (
                  <Pressable key={m.userId} direction="horizontal" radius="md" onPress={() => vm.addMember(m.userId)} testID={`create-channel-person-${m.userId}`}>
                    <Stack grow direction="horizontal" align="center" gap="2" paddingX="2" paddingY="1">
                      <Avatar name={m.name} src={m.avatarUrl} size="avatar.xs" square={m.isAgent} />
                      <Stack grow>
                        <Text variant="footnote" lines={1}>
                          {m.name}
                        </Text>
                      </Stack>
                      {m.organization === undefined ? null : (
                        <Text variant="caption" color="external.fg" lines={1}>
                          {m.organization}
                        </Text>
                      )}
                      <Icon name="plus" size="icon.sm" color="text.tertiary" />
                    </Stack>
                  </Pressable>
                ))
              )}
            </Stack>
          </Stack>
        ) : null}
        {s.error === undefined ? null : (
          <Text variant="footnote" color="status.danger" testID="create-channel-error">
            {tr(s.error)}
          </Text>
        )}
        <Stack direction="horizontal" align="center" justify="end" gap="2">
          <Button label={t("common.cancel")} variant="ghost" onPress={onClose} testID="create-channel-cancel" />
          <Button label={t("channels.create.submit")} variant="primary" icon="plus" loading={s.busy} disabled={!s.canSubmit} onPress={submit} testID="create-channel-submit" />
        </Stack>
      </Stack>
    </Sheet>
  );
}

// ---------- Browse channels ----------

const FILTERS: BrowseFilter[] = ["all", "joined", "other", "archived"];

export type BrowseChannelsSheetProps = {
  open: boolean;
  onClose: () => void;
  vm: BrowseChannelsViewModel;
  state: BrowseChannelsState;
  onOpenChannel: (channelId: string) => void;
  /** "Create channel" (absent when I may not). */
  onCreate?: () => void;
};

/** Public channels with member counts (and private ones I'm in): search, filter, join / leave, open. */
export function BrowseChannelsSheet({ open, onClose, vm, state: s, onOpenChannel, onCreate }: BrowseChannelsSheetProps) {
  const t = useT();
  const tr = useTr();
  const filterLabel = (f: BrowseFilter) =>
    f === "all" ? t("channels.browse.filter.all") : f === "joined" ? t("channels.browse.filter.joined") : f === "other" ? t("channels.browse.filter.other") : t("channels.browse.filter.archived");
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("channels.browse.title")}
      closeLabel={t("common.close")}
      detents={["large"]}
      headerActions={s.canCreate && onCreate !== undefined ? <Button label={t("channel.create")} icon="plus" size="sm" onPress={onCreate} testID="browse-create" /> : undefined}
      testID="browse-channels-sheet"
    >
      <Stack gap="3">
        <TextField value={s.query} onChange={(v) => vm.setQuery(v)} placeholder={t("channels.browse.search")} leadingIcon="search" autoFocus testID="browse-search" />
        <SegmentedControl
          variant="pills"
          size="sm"
          accessibilityLabel={t("db.toolbar.filter")}
          options={FILTERS.map((f) => ({ value: f, label: filterLabel(f) }))}
          value={s.filter}
          onChange={(f) => vm.setFilter(f)}
          testID="browse-filter"
        />
        <Stack gap="0.5" testID="browse-list">
          {s.rows.length === 0 ? (
            <Stack align="center" padding="6">
              <Text color="text.tertiary">{s.loading ? t("common.loading") : t("channels.browse.empty")}</Text>
            </Stack>
          ) : (
            s.rows.map((r) => {
              const busy = s.busy.includes(r.channelId);
              return (
                <Pressable key={r.channelId} direction="horizontal" radius="md" onPress={() => onOpenChannel(r.channelId)} testID={`browse-row-${r.channelId}`}>
                  <Stack grow direction="horizontal" align="center" gap="3" paddingX="2" paddingY="2">
                    <Icon name={r.archived ? "archive" : r.isPrivate ? "lock" : "hash"} size="icon.md" color="text.secondary" />
                    <Stack grow gap="0.5">
                      <Stack direction="horizontal" align="center" gap="2">
                        <Text weight="semibold" lines={1}>
                          {r.name}
                        </Text>
                        {r.isMember ? (
                          <Text variant="caption" color="status.success">
                            {t("channels.browse.joined")}
                          </Text>
                        ) : null}
                      </Stack>
                      <Text variant="footnote" color="text.secondary" lines={1}>
                        {[t("channel.members", { count: r.memberCount }), r.purpose ?? r.topic ?? ""].filter((x) => x !== "").join(" · ")}
                      </Text>
                    </Stack>
                    {r.canJoin ? (
                      <Button label={t("channels.browse.join")} size="sm" variant="primary" loading={busy} onPress={() => void vm.join(r.channelId)} testID={`browse-join-${r.channelId}`} />
                    ) : r.canLeave && !r.archived ? (
                      <Button label={t("channels.browse.leave")} size="sm" variant="ghost" onPress={() => vm.leave(r.channelId)} testID={`browse-leave-${r.channelId}`} />
                    ) : null}
                  </Stack>
                </Pressable>
              );
            })
          )}
        </Stack>
      </Stack>
      <Toast open={s.notice !== undefined} onClose={() => vm.dismissNotice()} message={s.notice === undefined ? "" : tr(s.notice)} closeLabel={t("common.close")} durationMs={4000} testID="browse-notice" />
    </Sheet>
  );
}

// ---------- Channel settings ----------

const LEVELS: NotifyLevel[] = ["default", "all", "mentions", "none"];

export type ChannelDetailsTab = "about" | "members" | "settings";

export type ChannelSettingsSheetProps = {
  open: boolean;
  onClose: () => void;
  vm: ChannelSettingsViewModel;
  state: ChannelSettingsState;
  /** DMs: "Add people" (starts a new conversation with more people); absent = not offered. */
  onAddPeople?: () => void;
  /** DMs: after "Close conversation" (e.g. leave the screen). */
  onClosedConversation?: () => void;
  /**
   * Channels: Slack's channel details with About / Members / Settings tabs — `members` is the Members tab's content
   * (ChannelMembersList), `tab` the one showing. Absent (DMs) = one page.
   */
  members?: ReactNode;
  tab?: ChannelDetailsTab;
  onTab?: (tab: ChannelDetailsTab) => void;
};

/**
 * Slack's channel details. Channels: About (name, topic, description, created by / on, Leave channel), Members (the
 * list, search, add, remove) and Settings (notifications, convert public ↔ private, archive). DMs: topic,
 * notifications, Add people, Close conversation, and for group DMs Convert to private channel.
 */
export function ChannelSettingsSheet({ open, onClose, vm, state: s, onAddPeople, onClosedConversation, members, tab = "about", onTab }: ChannelSettingsSheetProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const levelLabel = (l: NotifyLevel) =>
    l === "default" ? t("channels.settings.notify.default") : l === "all" ? t("channels.settings.notify.all") : l === "mentions" ? t("channels.settings.notify.mentions") : t("channels.settings.notify.none");
  const p = s.perms;
  const nameNote = s.nameError ?? s.nameHint;
  const tabbed = !s.isDm && members !== undefined && onTab !== undefined;
  const show = (x: ChannelDetailsTab) => !tabbed || tab === x;

  const archivedBanner = s.archived ? (
    <Stack direction="horizontal" align="center" gap="2" padding="3" radius="md" background="bg.surface" border="border.subtle" testID="channel-settings-archived">
      <Icon name="archive" size="icon.sm" color="text.secondary" />
      <Text variant="footnote" color="text.secondary">
        {t("channels.archived.banner")}
      </Text>
    </Stack>
  ) : null;

  const about = (
    <>
      {s.isDm ? null : p.canRename ? (
        <Stack gap="1">
          <TextField label={t("channels.create.name")} value={s.name} onChange={(v) => vm.setName(v)} leadingIcon={s.kind === "private" ? "lock" : "hash"} onSubmit={() => vm.save()} testID="channel-settings-name" />
          {nameNote === undefined ? null : (
            <Text variant="caption" color={s.nameError === undefined ? "text.tertiary" : "status.danger"} testID="channel-settings-name-note">
              {tr(nameNote)}
            </Text>
          )}
        </Stack>
      ) : (
        <Stack gap="1">
          <FieldLabel label={t("channels.create.name")} />
          <Text weight="semibold">{s.title}</Text>
        </Stack>
      )}
      {p.canEditTopic ? (
        <>
          <TextField label={t("channels.settings.topic")} value={s.topic} onChange={(v) => vm.setTopic(v)} placeholder={t("channels.settings.topicPlaceholder")} onSubmit={() => vm.save()} testID="channel-settings-topic" />
          {s.isDm ? null : (
            <TextField
              label={t("channels.create.description")}
              value={s.purpose}
              onChange={(v) => vm.setPurpose(v)}
              placeholder={t("channels.create.descriptionPlaceholder")}
              multiline
              testID="channel-settings-description"
            />
          )}
        </>
      ) : (
        <>
          {s.topic === "" ? null : (
            <Stack gap="1">
              <FieldLabel label={t("channels.settings.topic")} />
              <Text>{s.topic}</Text>
            </Stack>
          )}
          {s.isDm || s.purpose === "" ? null : (
            <Stack gap="1">
              <FieldLabel label={t("channels.create.description")} />
              <Text>{s.purpose}</Text>
            </Stack>
          )}
        </>
      )}
      {p.canEditTopic || p.canRename ? (
        <Stack direction="horizontal" align="center" justify="end" gap="2">
          <Button label={t("common.save")} variant="primary" disabled={!s.canSave} onPress={() => vm.save()} testID="channel-settings-save" />
        </Stack>
      ) : null}
      {s.createdBy === undefined || s.createdAt === undefined ? null : (
        <Stack gap="1" testID="channel-settings-created">
          <FieldLabel label={t("channels.settings.created")} />
          <Text color="text.secondary">
            {t("channels.settings.createdBy", { name: s.createdBy, date: new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(s.createdAt) })}
          </Text>
        </Stack>
      )}
      {tabbed && p.canLeave ? (
        <Stack direction="horizontal">
          <Button label={t("channels.settings.leave")} icon="leave" variant="destructive" onPress={() => vm.leave()} testID="channel-settings-leave" />
        </Stack>
      ) : null}
    </>
  );

  const settings = (
    <>
      {p.isMember ? (
        <Stack gap="2">
          <FieldLabel label={t("channels.settings.notifications")} />
          <Toggle label={t("channels.settings.mute")} value={s.muted} onChange={(v) => vm.setMuted(v)} testID="channel-settings-mute" />
          <SegmentedControl
            variant="pills"
            size="sm"
            accessibilityLabel={t("channels.settings.notifications")}
            options={LEVELS.map((l) => ({ value: l, label: levelLabel(l) }))}
            value={s.notify}
            onChange={(l) => vm.setNotify(l)}
            testID="channel-settings-notify"
          />
        </Stack>
      ) : null}
      {p.canConvert || p.canArchive || (p.canLeave && !tabbed) ? (
        <Stack gap="2">
          {p.canConvert ? (
            <Stack gap="1">
              <Button
                label={s.kind === "private" ? t("channels.settings.makePublic") : t("channels.settings.makePrivate")}
                icon={s.kind === "private" ? "hash" : "lock"}
                onPress={() => vm.setPrivate(s.kind !== "private")}
                testID="channel-settings-convert"
              />
              <Text variant="caption" color="text.tertiary">
                {s.kind === "private" ? t("channels.settings.makePublicHint") : t("channels.settings.makePrivateHint")}
              </Text>
            </Stack>
          ) : null}
          {p.canArchive ? (
            <Stack gap="1">
              <Button
                label={s.archived ? t("channels.settings.unarchive") : t("channels.settings.archive")}
                icon="archive"
                variant={s.archived ? "secondary" : "destructive"}
                onPress={() => vm.setArchived(!s.archived)}
                testID="channel-settings-archive"
              />
              {s.archived ? null : (
                <Text variant="caption" color="text.tertiary">
                  {t("channels.settings.archiveHint")}
                </Text>
              )}
            </Stack>
          ) : null}
          {p.canLeave && !tabbed ? <Button label={t("channels.settings.leave")} icon="leave" variant="ghost" onPress={() => vm.leave()} testID="channel-settings-leave" /> : null}
        </Stack>
      ) : null}
    </>
  );

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={tabbed ? `${s.kind === "private" ? "" : "#"}${s.title}` : t("channel.settings")}
      closeLabel={t("common.close")}
      testID="channel-settings-sheet"
    >
      <Stack gap="4">
        {tabbed ? (
          <SegmentedControl
            accessibilityLabel={t("channel.settings")}
            options={[
              { value: "about" as const, label: t("channels.details.about") },
              { value: "members" as const, label: t("channels.details.members"), count: s.memberCount },
              { value: "settings" as const, label: t("channels.details.settings") },
            ]}
            value={tab}
            onChange={(v) => onTab?.(v)}
            testID="channel-details-tabs"
          />
        ) : null}
        {archivedBanner}
        {show("about") ? about : null}
        {tabbed && tab === "members" ? members : null}
        {show("settings") ? settings : null}
        {s.isDm && (onAddPeople !== undefined || s.canClose || s.canConvertToChannel) ? (
          <Stack gap="3" testID="channel-settings-dm">
            {onAddPeople === undefined ? null : (
              <Stack gap="1">
                <Button label={t("dm.addPeople")} icon="invite" onPress={onAddPeople} testID="channel-settings-add-people" />
                <Text variant="caption" color="text.tertiary">
                  {t("dm.addPeopleHint")}
                </Text>
              </Stack>
            )}
            {s.canConvertToChannel ? (
              <Stack gap="1.5" testID="channel-settings-convert-dm">
                <FieldLabel label={t("dm.convert")} />
                <Text variant="caption" color="text.tertiary">
                  {t("dm.convertHint")}
                </Text>
                <TextField
                  label={t("dm.convertName")}
                  value={s.convertName}
                  onChange={(v) => vm.setConvertName(v)}
                  leadingIcon="lock"
                  placeholder={t("channels.create.namePlaceholder")}
                  error={s.convertError === undefined ? undefined : tr(s.convertError)}
                  onSubmit={() => vm.convertToChannel()}
                  testID="channel-settings-convert-name"
                />
                <Stack direction="horizontal" justify="end">
                  <Button label={t("dm.convertAction")} icon="lock" disabled={s.convertName.trim() === ""} onPress={() => vm.convertToChannel()} testID="channel-settings-convert-dm-submit" />
                </Stack>
              </Stack>
            ) : null}
            {s.canClose ? (
              <Button
                label={t("dm.close")}
                icon="close"
                variant="ghost"
                onPress={() => {
                  vm.closeConversation();
                  onClose();
                  onClosedConversation?.();
                }}
                testID="channel-settings-close-dm"
              />
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      <Toast open={s.notice !== undefined} onClose={() => vm.dismissNotice()} message={s.notice === undefined ? "" : tr(s.notice)} closeLabel={t("common.close")} durationMs={3000} testID="channel-settings-notice" />
    </Sheet>
  );
}

// ---------- Channel screen notices ----------

/** Read-only notice in place of the composer of an archived channel. */
export function ArchivedNotice({ testID }: { testID: string }) {
  const t = useT();
  return (
    <Card padding="3" testID={testID}>
      <Stack direction="horizontal" align="center" justify="center" gap="2">
        <Icon name="archive" size="icon.sm" color="text.secondary" />
        <Text variant="footnote" color="text.secondary">
          {t("channels.archived.banner")}
        </Text>
      </Stack>
    </Card>
  );
}

/** "You're viewing #design" + Join, in place of the composer of a public channel I'm not in. */
export function JoinChannelBar({ name, onJoin, testID }: { name: string; onJoin: () => void; testID: string }) {
  const t = useT();
  return (
    <Card padding="3" testID={testID}>
      <Stack direction="horizontal" align="center" justify="center" gap="3">
        <Text variant="footnote" color="text.secondary">
          {t("channels.join.prompt", { channel: name })}
        </Text>
        <Button label={t("channels.join.button")} variant="primary" size="sm" onPress={onJoin} testID={`${testID}-join`} />
      </Stack>
    </Card>
  );
}

// ---------- Sidebar folders ----------

export type FolderEditorSheetProps = {
  editor: FolderEditor | undefined;
  /** Icons a folder can show instead of an emoji. */
  icons: readonly string[];
  onName: (name: string) => void;
  onEmoji: (emoji: string | undefined) => void;
  onIcon: (icon: string) => void;
  onSave: () => void;
  onClose: () => void;
};

/** Create or edit one of my sidebar folders: name, and an emoji or an icon. */
export function FolderEditorSheet({ editor, icons, onName, onEmoji, onIcon, onSave, onClose }: FolderEditorSheetProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const [picking, setPicking] = useState(false);
  const e = editor;
  const current = e?.emoji === undefined ? e?.icon ?? "folder" : undefined;
  const emojiButton = (
    <Button label={e?.emoji ?? t("sidebar.folder.look")} icon={e?.emoji === undefined ? "emoji" : undefined} variant="secondary" onPress={() => setPicking(true)} testID="folder-editor-emoji" />
  );
  return (
    <Sheet open={e !== undefined} onClose={onClose} title={e?.mode === "edit" ? t("sidebar.folder.edit") : t("sidebar.folder.new")} closeLabel={t("common.close")} testID="folder-editor-sheet">
      <Stack gap="3">
        <TextField
          label={t("sidebar.folder.nameLabel")}
          value={e?.name ?? ""}
          onChange={onName}
          placeholder={t("sidebar.folder.namePlaceholder")}
          autoFocus
          onSubmit={onSave}
          testID="folder-editor-name"
        />
        {e !== undefined && e.showError && e.error !== undefined ? (
          <Text variant="caption" color="status.danger" testID="folder-editor-error">
            {tr(e.error)}
          </Text>
        ) : null}
        <Stack gap="1.5">
          <FieldLabel label={t("sidebar.folder.look")} />
          <Stack direction="horizontal" align="center" gap="1" wrap>
            <Popover open={picking} onClose={() => setPicking(false)} side="bottom" align="start" anchor={emojiButton}>
              <EmojiPicker
                onSelect={(x) => {
                  setPicking(false);
                  onEmoji(x);
                }}
                searchPlaceholder={t("sidebar.folder.emojiSearch")}
                locale={locale}
                testID="folder-editor-emoji-picker"
              />
            </Popover>
            {icons.map((icon) => (
              <Pressable
                key={icon}
                radius="md"
                selected={current === icon}
                onPress={() => onIcon(icon)}
                accessibilityLabel={`${t("sidebar.folder.useIcon")}: ${icon}`}
                testID={`folder-editor-icon-${icon}`}
              >
                <Stack padding="1.5" radius="md" border={current === icon ? "accent.text" : "border.subtle"}>
                  <Icon name={icon as IconName} size="icon.sm" color={current === icon ? "text.link" : "text.secondary"} />
                </Stack>
              </Pressable>
            ))}
          </Stack>
        </Stack>
        <Stack direction="horizontal" align="center" justify="end" gap="2">
          <Button label={t("common.cancel")} variant="ghost" onPress={onClose} testID="folder-editor-cancel" />
          <Button label={e?.mode === "edit" ? t("common.save") : t("sidebar.folder.createSubmit")} variant="primary" onPress={onSave} testID="folder-editor-save" />
        </Stack>
      </Stack>
    </Sheet>
  );
}

export type MoveToFolderSheetProps = {
  moving: MovePicker | undefined;
  onPick: (sectionId: string) => void;
  onClose: () => void;
};

const TARGET_ICONS: Record<string, IconName> = { starred: "star", channels: "message", dms: "users", docs: "page" };

/** "Move to folder…": the keyboard / touch path for what drag and drop does. */
export function MoveToFolderSheet({ moving, onPick, onClose }: MoveToFolderSheetProps) {
  const t = useT();
  const tr = useTr();
  return (
    <Sheet open={moving !== undefined} onClose={onClose} title={t("sidebar.move.title", { name: moving?.name ?? "" })} closeLabel={t("common.close")} testID="move-folder-sheet">
      <Stack gap="0.5">
          {(moving?.targets ?? []).map((target) => (
            <Pressable key={target.id} direction="horizontal" radius="md" onPress={() => onPick(target.id)} testID={`move-folder-${target.id}`}>
              <Stack grow direction="horizontal" align="center" gap="2.5" paddingX="2" paddingY="1.5">
                {target.emoji === undefined ? (
                  <Icon name={TARGET_ICONS[target.id] ?? ((target.icon ?? "folder") as IconName)} size="icon.sm" color="text.secondary" />
                ) : (
                  <Text variant="footnote">{target.emoji}</Text>
                )}
                <Text lines={1}>{tr(target.title)}</Text>
              </Stack>
            </Pressable>
          ))}
          <Pressable direction="horizontal" radius="md" onPress={() => onPick("new")} testID="move-folder-new">
            <Stack grow direction="horizontal" align="center" gap="2.5" paddingX="2" paddingY="1.5">
              <Icon name="plus" size="icon.sm" color="text.secondary" />
              <Text color="text.secondary">{t("sidebar.move.newFolder")}</Text>
            </Stack>
          </Pressable>
      </Stack>
    </Sheet>
  );
}

/** An empty folder: where conversations and pages can be dropped. */
export function EmptyFolderHint({ testID }: { testID: string }) {
  const t = useT();
  return (
    <Stack paddingX="3" paddingY="1" testID={testID}>
      <Text variant="caption" color="text.tertiary">
        {t("sidebar.folder.empty")}
      </Text>
    </Stack>
  );
}
